import React, { useState, useEffect } from 'react';
import { convertGoogleDriveUrlForDisplay, getPlaceholderImage } from '../utils/imageUtils';
import './VariantModal.css';

export default function VariantModal({ product, isOpen, onClose, onAddToCart }) {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  // Reset selected variant when product or modal opens
  React.useEffect(() => {
    if (isOpen && product) {
      setSelectedVariant(product.variants?.[0] || null);
      setQuantity(1);
    }
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  const hasVariants = product.variants && product.variants.length > 1;
  const variantCount = product.variants?.length || 0;
  // Only use variant prices when variants exist, otherwise use product prices
  const currentPrice = hasVariants
    ? (selectedVariant?.price || product.variants?.[0]?.price || 0)
    : (product.price || 0);
  const currentMrp = hasVariants
    ? (selectedVariant?.mrp || product.variants?.[0]?.mrp || currentPrice)
    : (product.mrp || currentPrice);
  const discount = currentMrp > currentPrice ? Math.round(((currentMrp - currentPrice) / currentMrp) * 100) : 0;

  const handleAddToCart = async () => {
    if (adding) return;
    
    // Validate product ID before attempting to add to cart
    if (!product || !product._id) {
      alert('Error: Product ID is missing. Please refresh the page and try again.');
      return;
    }
    
    try {
      setAdding(true);
      const variant = selectedVariant?.sku ? {
        sku: selectedVariant.sku,
        sizeLabel: selectedVariant.label,
        price: selectedVariant.price,
        mrp: selectedVariant.mrp
      } : null;
      
      await onAddToCart(product._id, variant, quantity);
      onClose();
    } catch (error) {
      console.error('Error adding to cart:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to add to cart. Please try again.';
      alert(errorMsg);
    } finally {
      setAdding(false);
    }
  };

  const handleQuantityChange = (delta) => {
    const newQuantity = Math.max(1, quantity + delta);
    setQuantity(newQuantity);
  };

  return (
    <div className="variant-modal-overlay" onClick={onClose}>
      <div className="variant-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="variant-modal-close" onClick={onClose}>×</button>
        
        <div className="variant-modal-header">
          <img 
            src={product.images?.[0] ? convertGoogleDriveUrlForDisplay(product.images[0]) : getPlaceholderImage(200, 200, 'No Image')} 
            alt={product.name}
            className="variant-modal-image"
            onError={(e) => {
              // Prevent infinite retry loops
              if (e.target.dataset.retryCount) {
                e.target.src = getPlaceholderImage(200, 200, 'No Image');
                e.target.onerror = null; // Stop retrying
                return;
              }
              e.target.dataset.retryCount = '1';
              
              const originalSrc = e.target.src;
              
              // Skip problematic URLs
              if (originalSrc.includes('lh3.googleusercontent.com') || 
                  originalSrc.includes('drive.google.com/uc?export=download')) {
                e.target.src = getPlaceholderImage(200, 200, 'No Image');
                e.target.onerror = null;
                return;
              }
              
              // Try thumbnail method if view fails (only for Google Drive URLs)
              if (originalSrc.includes('drive.google.com')) {
                const fileIdMatch = originalSrc.match(/[?&]id=([a-zA-Z0-9_-]+)/) || 
                                  originalSrc.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                if (fileIdMatch && fileIdMatch[1] && !originalSrc.includes('thumbnail')) {
                  e.target.src = `https://drive.google.com/thumbnail?id=${fileIdMatch[1]}&sz=w400`;
                  return;
                }
              }
              
              e.target.src = getPlaceholderImage(200, 200, 'No Image');
              e.target.onerror = null; // Stop retrying
            }}
          />
          <div className="variant-modal-info">
            <h3>{product.name}</h3>
            {/* Only show price in header if no variants exist */}
            {!hasVariants && (
              <div className="variant-modal-price">
                <span className="current-price">₹{currentPrice}</span>
                {currentMrp > currentPrice && (
                  <>
                    <span className="variant-modal-mrp">₹{currentMrp}</span>
                    {discount > 0 && (
                      <span className="variant-modal-discount">{discount}% OFF</span>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {hasVariants && (
          <div className="variant-modal-options">
            <h4>Select {variantCount} options:</h4>
            <div className="variant-options-grid">
              {product.variants.map((variant, index) => {
                const isSelected = selectedVariant?.sku === variant.sku || 
                                  (!selectedVariant?.sku && index === 0);
                const variantDiscount = variant.mrp && variant.mrp > variant.price 
                  ? Math.round(((variant.mrp - variant.price) / variant.mrp) * 100) 
                  : 0;
                
                return (
                  <button
                    key={variant.sku || index}
                    className={`variant-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedVariant(variant)}
                    disabled={variant.stock === 0}
                  >
                    <span className="variant-label">{variant.label || variant.sizeLabel}</span>
                    <div className="variant-price-wrapper">
                      <span className="variant-price">₹{variant.price}</span>
                      {variant.mrp && variant.mrp > 0 ? (
                        variant.mrp > variant.price ? (
                          <>
                            <span className="variant-mrp">₹{variant.mrp}</span>
                            {variantDiscount > 0 && (
                              <span className="variant-discount">{variantDiscount}% OFF</span>
                            )}
                          </>
                        ) : (
                          <span className="variant-mrp-same">MRP: ₹{variant.mrp}</span>
                        )
                      ) : null}
                    </div>
                    {variant.stock === 0 && (
                      <span className="variant-out-of-stock">Out of stock</span>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Show selected variant price details */}
            {selectedVariant && (
              <div className="selected-variant-price-section">
                <div className="variant-modal-price">
                  <span className="current-price">₹{selectedVariant.price}</span>
                  {selectedVariant.mrp && selectedVariant.mrp > 0 && (
                    selectedVariant.mrp > selectedVariant.price ? (
                      <>
                        <span className="variant-modal-mrp">₹{selectedVariant.mrp}</span>
                        {discount > 0 && (
                          <span className="variant-modal-discount">{discount}% OFF</span>
                        )}
                      </>
                    ) : (
                      <span className="variant-modal-mrp-same">MRP: ₹{selectedVariant.mrp}</span>
                    )
                  )}
                </div>
                {selectedVariant.mrp && selectedVariant.mrp > selectedVariant.price && (
                  <p className="savings-text">You save ₹{selectedVariant.mrp - selectedVariant.price}</p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="variant-modal-quantity">
          <label>Quantity:</label>
          <div className="quantity-controls">
            <button onClick={() => handleQuantityChange(-1)} disabled={quantity <= 1}>−</button>
            <span>{quantity}</span>
            <button onClick={() => handleQuantityChange(1)}>+</button>
          </div>
        </div>

        <div className="variant-modal-actions">
          <button 
            className="variant-modal-add-btn"
            onClick={handleAddToCart}
            disabled={adding || (selectedVariant && selectedVariant.stock === 0)}
          >
            {adding ? 'Adding...' : `Add to Cart - ₹${currentPrice * quantity}`}
          </button>
        </div>
      </div>
    </div>
  );
}

